'use client';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import PromoSlotPdp from '@/components/promoSlot/PromoSlotPdp';

import {
  BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE,
  PUF_PLAY_KIT_DISCOUNT_RATE,
} from '@/core/constants';

import { FeatureFlag } from '@/core/featureFlags';
import { TypeProduct } from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';

import { ShopifyVariant, ShopifyProduct } from '@/lib/shopify';

import { checkIsBabyAgeRange } from '@/utils/checkIsBabyAgeRange';
import { removeCentsFromAmount } from '@/utils/currency';
import { formatMoney, formatMoneyWithSmartDecimals } from '@/utils/helpers';

import { ConditionalRichText } from '../ConditionalRichText';
import { RadioButton } from '../RadioButton';
import { RichText } from '../RichText';

import styles from './ProductOptionsRadio.module.scss';

interface ProductOptionRadioInterface {
  products: TypeProduct[];
  productSelected: TypeProduct;
  shopifyProducts: ShopifyProduct[];
  onChange: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
  isBfcmEnabled?: boolean;
  originalProduct?: TypeProduct; // Original product for age range checking
}

function ProductOptionRadio({
  products,
  productSelected,
  shopifyProducts,
  isBfcmEnabled = false,
  onChange,
  originalProduct,
}: ProductOptionRadioInterface) {
  const locale = useLocale();

  const pufDiscountPlayKitPdp =
    useFeature(FeatureFlag.MONEY_PUF_DISCOUNT_PLAY_KIT_PDP) || 'off';

  const bfcmDiscountPlayKitPdpLargeEnabled = ['hold', 'control'].includes(
    pufDiscountPlayKitPdp,
  );

  const pufDiscountPlayKitPdpEnabled = pufDiscountPlayKitPdp === 'treatment';

  const hasDiscountPlayKitPdpEnabled =
    bfcmDiscountPlayKitPdpLargeEnabled || pufDiscountPlayKitPdpEnabled;

  const isEarlyDecSubCampaignEnabled = useFeature(
    'money_early-dec-sub-campaign',
  );

  // Check if this is a Baby kit - use original product if provided, otherwise use first product
  const productToCheck = originalProduct || products[0];

  const ageRangeToCheck = productToCheck?.fields?.ageRange;

  const isBabyKit = productToCheck && checkIsBabyAgeRange(ageRangeToCheck);

  // Baby Treatment: Show only Subscribe & Save for Baby kits when its specific flag variant is 'b'
  const removeSingleKitBabyFlag = useFeature(
    FeatureFlag.SUB_SQUAD_REMOVE_SINGLE_PLAY_KIT,
    'off',
  );

  const shouldShowTreatmentBaby = removeSingleKitBabyFlag === 'b' && isBabyKit;

  // Toddler Treatment: Show only Subscribe & Save for Toddler kits when its specific flag variant is 'b'
  const removeSingleKitToddlerFlag = useFeature(
    FeatureFlag.SUB_SQUAD_REMOVE_SINGLE_PLAY_KIT_TODDLER,
    'off',
  );

  const shouldShowTreatmentToddler =
    removeSingleKitToddlerFlag === 'b' && !isBabyKit;

  const shouldShowTreatment =
    (shouldShowTreatmentBaby || shouldShowTreatmentToddler) &&
    products.length > 0;

  if (shouldShowTreatment) {
    const subscribeProduct = products[0];

    const shopifyProduct = shopifyProducts[0];

    const variant = shopifyProduct.variants[0];

    const { optionDetails } = subscribeProduct.fields;

    const productTranslations = subscribeProduct.fields.translations?.fields;

    const priceAmount = variant.price?.amount;

    let displayPrice: string | null = priceAmount
      ? formatMoneyWithSmartDecimals(priceAmount, locale)
      : variant.formattedPrice;

    // Apply discount if enabled
    if (hasDiscountPlayKitPdpEnabled) {
      const priceAmountValue =
        typeof priceAmount === 'string' ? parseFloat(priceAmount) : priceAmount;

      if (priceAmountValue) {
        let discountRate = 0;

        if (bfcmDiscountPlayKitPdpLargeEnabled) {
          const ageRangeKey = isBabyKit ? 'baby' : 'toddler';

          discountRate =
            BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE[
              process.env
                .NEXT_PUBLIC_REGION as keyof typeof BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE
            ][ageRangeKey];
        }

        if (pufDiscountPlayKitPdpEnabled) {
          discountRate =
            PUF_PLAY_KIT_DISCOUNT_RATE[
              process.env
                .NEXT_PUBLIC_REGION as keyof typeof PUF_PLAY_KIT_DISCOUNT_RATE
            ];
        }

        const discountedValue = Math.round(
          priceAmountValue * (1 - discountRate),
        );

        displayPrice = formatMoneyWithSmartDecimals(discountedValue, locale);
      }
    }

    const shouldShowPromotionalContent =
      (isBfcmEnabled && productTranslations?.secondaryBadge) ||
      isEarlyDecSubCampaignEnabled;

    return (
      <div className="mb-4" data-testid="product-options">
        <div className="mb-4">
          {/* Price section */}
          <div
            className={classNames(
              'text-charcoal font-bold mb-4',
              hasDiscountPlayKitPdpEnabled && 'flex items-baseline',
            )}
          >
            {hasDiscountPlayKitPdpEnabled && (
              <span className="text-base mr-1">
                {productTranslations?.subscriptionPricePrefix}{' '}
              </span>
            )}
            <span className="text-h-x5 lg:text-h-x4">
              {displayPrice && removeCentsFromAmount(displayPrice)}
            </span>
          </div>

          {/* Divider after price */}
          <div className="border-t border-gray-midtone mb-4" />

          {/* Promotional content (BFCM) */}
          {shouldShowPromotionalContent &&
            productTranslations?.bfcmProductOptionsRadioText && (
              <ConditionalRichText
                content={
                  productTranslations.bfcmProductOptionsRadioText as Document
                }
                addParagraphMargins={false}
                classes="!mb-4"
              />
            )}

          {/*Optional heading for details*/}
          {productTranslations?.optionDetailsHeading && (
            <h3 className="font-bold text-lg mb-2">
              {productTranslations.optionDetailsHeading}
            </h3>
          )}

          {/* Benefits list with charcoal checkmarks */}
          {optionDetails && (
            <div
              className={classNames(
                styles.treatmentBenefits,
                'font-normal text-base rte',
              )}
            >
              <RichText
                content={optionDetails as Document}
                addParagraphMargins={false}
              />
            </div>
          )}

          {/* GWP Promotion after benefits - in purple with "Plus:" prefix */}
          <PromoSlotPdp
            className="mt-4"
            slotId="pdp-top-option"
            isPlayKit={subscribeProduct.fields.isPlayKit}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="product-options">
      {products?.map((product, index) => {
        const id = product.sys.id;

        const shopifyProduct = shopifyProducts[index];

        const variant = shopifyProduct.variants[0];

        // The first variant is always used because the others
        // do not fit into the radio options design proposed
        const formattedPrice = variant.formattedPrice;

        const { title, optionDetails, strikethroughPrice, isPlayKit } =
          product.fields;

        const checked = productSelected?.sys.id === id;

        const productTranslations = product.fields.translations?.fields;

        const shouldShowBfcmContent =
          isBfcmEnabled &&
          productTranslations?.secondaryBadge &&
          productTranslations?.badgeStandalone;

        const shouldShowEarlyDecSubCampaignContent =
          isEarlyDecSubCampaignEnabled &&
          product.fields.translations?.fields.secondaryBadge &&
          product.fields.translations?.fields.badgeStandalone;

        const shouldShowPromotionalContent =
          shouldShowBfcmContent || isEarlyDecSubCampaignEnabled;

        const priceAmount = variant.price?.amount;

        let displayPrice: string | null = priceAmount
          ? formatMoneyWithSmartDecimals(priceAmount, locale)
          : formattedPrice;

        // Get the first variant to apply the discounted price
        const isSubscribeAndSave = index === 0;

        // This controls the price display during BFCM through a feature flag,
        // the discounted price is shown instead of the original price
        if (isPlayKit && isSubscribeAndSave && hasDiscountPlayKitPdpEnabled) {
          // Some variant price amounts returns a string (e.g: '84.0') instead of number
          const priceAmountValue =
            typeof priceAmount === 'string'
              ? parseFloat(priceAmount)
              : priceAmount;

          if (priceAmountValue) {
            let discountRate = 0;

            if (bfcmDiscountPlayKitPdpLargeEnabled) {
              // The discounted price is calculated based on the age range and region
              const ageRangeKey = checkIsBabyAgeRange(product.fields.ageRange)
                ? 'baby'
                : 'toddler';

              discountRate =
                BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE[
                  process.env
                    .NEXT_PUBLIC_REGION as keyof typeof BFCM_PLAY_KIT_LARGE_DISCOUNT_RATE
                ][ageRangeKey];
            }

            if (pufDiscountPlayKitPdpEnabled) {
              // The discounted price is calculated based on the region
              discountRate =
                PUF_PLAY_KIT_DISCOUNT_RATE[
                  process.env
                    .NEXT_PUBLIC_REGION as keyof typeof PUF_PLAY_KIT_DISCOUNT_RATE
                ];
            }

            const discountedValue = Math.round(
              priceAmountValue * (1 - discountRate),
            );

            displayPrice = formatMoneyWithSmartDecimals(
              discountedValue,
              locale,
            );
          }
        }

        return (
          <RadioButton
            key={id}
            id={id}
            Content={() => (
              <div className="flex-auto">
                <div className="flex items-center">
                  <div className="text-[1.25rem] pr-8 flex-auto">{title}</div>

                  <div className="flex flex-none items-center">
                    {strikethroughPrice && !hasDiscountPlayKitPdpEnabled && (
                      <div className="text-lg line-through font-normal text-gray-light mr-1">
                        {formatMoney(strikethroughPrice, locale, true)}
                      </div>
                    )}

                    <div
                      className={classNames(
                        !!strikethroughPrice && 'text-purple',
                      )}
                    >
                      {strikethroughPrice && hasDiscountPlayKitPdpEnabled && (
                        <span className="text-sm lg:text-base">
                          {productTranslations?.subscriptionPricePrefix}{' '}
                        </span>
                      )}

                      <span className="text-h-x5 lg:text-h-x4">
                        {displayPrice && removeCentsFromAmount(displayPrice)}
                      </span>
                    </div>
                  </div>
                </div>

                {optionDetails && (
                  <div className="font-normal text-base mt-4 -ml-9 rte">
                    {index === 0 && (
                      <PromoSlotPdp
                        className="mb-4"
                        slotId="pdp-top-option"
                        isPlayKit={isPlayKit}
                      />
                    )}

                    {shouldShowPromotionalContent &&
                      product.fields.translations?.fields
                        .bfcmProductOptionsRadioText && (
                        <ConditionalRichText
                          content={
                            productTranslations?.bfcmProductOptionsRadioText as Document
                          }
                          addParagraphMargins={false}
                          classes="!mb-4"
                        />
                      )}

                    <RichText
                      content={optionDetails as Document}
                      addParagraphMargins={false}
                    />
                  </div>
                )}
              </div>
            )}
            className={styles.optionRadio}
            checked={checked}
            onChange={() => {
              onChange(product, shopifyProduct, variant);
            }}
            boldLabel
            badge={
              shouldShowBfcmContent || shouldShowEarlyDecSubCampaignContent
                ? product.fields.translations?.fields.secondaryBadge
                : product.fields.translations?.fields.badgeStandalone
            }
            experimentSelectors={{
              context: 'playkit-pdp',
              element: 'product-option-radio',
              index: index.toString(),
            }}
          />
        );
      })}
    </div>
  );
}

export default ProductOptionRadio;
